import { API_BASE_URL } from "./config";

export type EnquiryType = "general" | "viewing" | "valuation" | "alert";

export interface ContactPayload {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  sourcePage?: string;
  propertyId?: number;
  mri_id?: number;
  enquiryType?: EnquiryType;
  wechat?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { message?: string };
}

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      theme: "auto";
      callback: (token: string) => void;
      "error-callback": () => void;
      "expired-callback": () => void;
    },
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY ??
  (process.env.NODE_ENV === "production" ? "" : "1x00000000000000000000AA");
let turnstileScriptPromise: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-letting-engine-turnstile="true"]',
    );
    const script = existing ?? document.createElement("script");
    const timeout = window.setTimeout(
      () => reject(new Error("Security check took too long to load. Please try again.")),
      10_000,
    );
    script.addEventListener("load", () => {
      window.clearTimeout(timeout);
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Security check failed to load."));
    }, { once: true });
    script.addEventListener("error", () => {
      window.clearTimeout(timeout);
      reject(new Error("Security check failed to load. Please check your connection."));
    }, { once: true });
    if (!existing) {
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.lettingEngineTurnstile = "true";
      document.head.appendChild(script);
    }
  }).catch((error) => {
    turnstileScriptPromise = undefined;
    throw error;
  });
  return turnstileScriptPromise;
}

async function requestTurnstileToken(): Promise<string> {
  if (!TURNSTILE_SITE_KEY) {
    throw new Error("Security check is not configured. Please contact us by phone.");
  }
  const turnstile = await loadTurnstile();

  return new Promise((resolve, reject) => {
    const overlay = document.createElement("div");
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.style.cssText = "position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.62);padding:20px";
    const panel = document.createElement("div");
    panel.style.cssText = "width:min(100%,420px);background:#fff;padding:28px;border-radius:4px;box-shadow:0 24px 70px rgba(0,0,0,.28);font-family:Manrope,Arial,sans-serif";
    panel.innerHTML = `
      <h2 style="margin:0 0 10px;font-size:25px">Security check</h2>
      <p style="margin:0 0 20px;color:#475569;line-height:1.5">Please confirm you are human before we email your verification code.</p>
      <div data-turnstile-container style="min-height:65px;display:flex;justify-content:center"></div>
      <div style="display:flex;justify-content:flex-end;margin-top:18px">
        <button type="button" data-cancel style="padding:10px 16px;border:1px solid #cbd5e1;background:#fff;cursor:pointer">Cancel</button>
      </div>`;
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    let widgetId: string | undefined;
    let settled = false;
    const finish = (token?: string, error?: Error) => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKeyDown);
      if (widgetId) turnstile.remove(widgetId);
      overlay.remove();
      if (token) resolve(token);
      else reject(error ?? new Error("Security check was cancelled. Your enquiry was not sent."));
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") finish();
    };
    panel.querySelector("[data-cancel]")!.addEventListener("click", () => finish());
    document.addEventListener("keydown", onKeyDown);

    widgetId = turnstile.render(
      panel.querySelector<HTMLElement>("[data-turnstile-container]")!,
      {
        sitekey: TURNSTILE_SITE_KEY,
        action: "contact_enquiry",
        theme: "auto",
        callback: (token) => finish(token),
        "error-callback": () => finish(undefined, new Error("Security check failed. Please try again.")),
        "expired-callback": () => finish(undefined, new Error("Security check expired. Please try again.")),
      },
    );
  });
}

async function readResponse<T>(res: Response): Promise<ApiResponse<T>> {
  const body = (await res.json().catch(() => ({}))) as ApiResponse<T>;
  if (!res.ok) {
    throw new Error(body.error?.message ?? `Contact failed (${res.status})`);
  }
  return body;
}

function askForVerificationCode(email: string): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.style.cssText = "position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.62);padding:20px";

    const panel = document.createElement("form");
    panel.style.cssText = "width:min(100%,420px);background:#fff;padding:28px;border-radius:4px;box-shadow:0 24px 70px rgba(0,0,0,.28);font-family:Manrope,Arial,sans-serif";
    panel.innerHTML = `
      <h2 style="margin:0 0 10px;font-size:25px">Verify your email</h2>
      <p style="margin:0 0 20px;color:#475569;line-height:1.5">We sent a six-digit code to <strong></strong>. Enter it below to send your enquiry.</p>
      <label style="display:block;font-size:13px;margin-bottom:7px" for="contact-verification-code">Verification code</label>
      <input id="contact-verification-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]{6}" required style="box-sizing:border-box;width:100%;padding:12px;border:1px solid #94a3b8;font-size:20px;letter-spacing:5px" />
      <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:22px">
        <button type="button" data-cancel style="padding:10px 16px;border:1px solid #cbd5e1;background:#fff;cursor:pointer">Cancel</button>
        <button type="submit" style="padding:10px 16px;border:1px solid #d5a62e;background:#eabf47;color:#111827;font-weight:700;cursor:pointer">Verify and send</button>
      </div>`;
    panel.querySelector("strong")!.textContent = email;
    const input = panel.querySelector("input") as HTMLInputElement;

    const finish = (value: string | null) => {
      document.removeEventListener("keydown", onKeyDown);
      overlay.remove();
      resolve(value);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") finish(null);
    };
    panel.addEventListener("submit", (event) => {
      event.preventDefault();
      if (input.checkValidity()) finish(input.value);
      else input.reportValidity();
    });
    panel.querySelector("[data-cancel]")!.addEventListener("click", () => finish(null));
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    document.addEventListener("keydown", onKeyDown);
    input.focus();
  });
}

export async function submitContact(payload: ContactPayload): Promise<void> {
  const turnstileToken = await requestTurnstileToken();
  const initialResponse = await fetch(`${API_BASE_URL}/api/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, website: "", turnstileToken }),
  });
  const initial = await readResponse<{
    verificationRequired?: boolean;
    verificationId?: string;
  }>(initialResponse);

  if (!initial.data?.verificationRequired || !initial.data.verificationId) {
    return;
  }

  const code = await askForVerificationCode(payload.email);
  if (!code) throw new Error("Email verification was cancelled. Your enquiry was not sent.");

  const verificationResponse = await fetch(`${API_BASE_URL}/api/contact/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ verificationId: initial.data.verificationId, code }),
  });
  const verification = await readResponse<{ verificationToken: string }>(verificationResponse);
  if (!verification.data?.verificationToken) throw new Error("Email verification failed.");

  const finalResponse = await fetch(`${API_BASE_URL}/api/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, website: "", verificationToken: verification.data.verificationToken }),
  });
  await readResponse(finalResponse);
}
